import type { Chart } from 'chart.js';
import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DESIRED_BAR_HEIGHT = 28;
const MAX_CHART_HEIGHT = 15000;
const SHOW_MORE_BUTTON_HEIGHT = 40;
const CHART_OVERHEAD_PX = 20;

type UseBarChartShowMoreOptions = {
  labels: string[] | undefined;
  isVertical: boolean;
  isDashboard: boolean;
  chartRef?: RefObject<Chart | null>;
};

export function useBarChartShowMore({
  labels,
  isVertical,
  isDashboard,
  chartRef,
}: UseBarChartShowMoreOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [expanded, setExpanded] = useState(false);

  const isHorizontal = !isVertical;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || isVertical || !isDashboard) return;

    setContainerHeight(el.clientHeight);

    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        setContainerHeight(el.clientHeight);
      });
    });
    resizeObserverRef.current.observe(el);

    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, [isVertical, isDashboard]);

  const maxVisible = useMemo(() => {
    if (!isDashboard || !isHorizontal || containerHeight === 0) return Infinity;
    const available =
      containerHeight - CHART_OVERHEAD_PX - SHOW_MORE_BUTTON_HEIGHT;
    return Math.max(1, Math.floor(available / DESIRED_BAR_HEIGHT));
  }, [isDashboard, isHorizontal, containerHeight]);

  const labelsKey = labels?.join('\0') ?? '';
  useEffect(() => {
    setExpanded(false);
  }, [labelsKey]);

  const totalCount = labels?.length ?? 0;
  const hasMore = isDashboard && isHorizontal && totalCount > maxVisible;
  const visibleCount = expanded ? totalCount : Math.min(totalCount, maxVisible);

  const expandedChartHeight =
    isDashboard && isHorizontal && expanded && hasMore
      ? Math.min(
          totalCount * DESIRED_BAR_HEIGHT + CHART_OVERHEAD_PX,
          MAX_CHART_HEIGHT,
        )
      : undefined;

  const collapsedChartHeight =
    isDashboard && isHorizontal && hasMore && !expanded
      ? visibleCount * DESIRED_BAR_HEIGHT + CHART_OVERHEAD_PX
      : undefined;

  const [chartAreaLeft, setChartAreaLeft] = useState(0);

  useEffect(() => {
    if (!isDashboard || !isHorizontal || !chartRef?.current) return;

    let retryCount = 0;
    let rafId = 0;

    const measure = () => {
      const left = chartRef.current?.chartArea?.left;
      if (left != null && left > 0) {
        setChartAreaLeft((prev) => (prev === left ? prev : left));
      } else if (retryCount < 5) {
        retryCount += 1;
        rafId = requestAnimationFrame(measure);
      }
    };

    rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
     
  }, [isDashboard, isHorizontal, visibleCount, expanded, chartRef]);

  return {
    containerRef,
    expanded,
    setExpanded,
    hasMore,
    visibleCount,
    totalCount,
    maxVisible,
    expandedChartHeight,
    collapsedChartHeight,
    isHorizontal,
    chartAreaLeft,
  };
}
